import { PennylaneAggregationLevelEnum } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import type { AggregationLevelGroupInput } from './aggregation-level-group.type';
import { resolveGroupVatIds } from './aggregation-level-group.type';

export type ResolveVatRateLabel = (vatId: Id) => string;

export type AggregationLevelVatWarnings = Readonly<
  Record<PennylaneAggregationLevelEnum, readonly string[]>
>;

function distinctInOrder(vatIds: readonly Id[]): readonly Id[] {
  return [...new Set(vatIds)];
}

function formatVatRateList(
  vatIds: readonly Id[],
  resolveVatRateLabel: ResolveVatRateLabel,
): string {
  const [firstLabel, ...otherLabels] = vatIds.map(resolveVatRateLabel);

  if (!firstLabel) {
    return '';
  }

  return otherLabels.reduce(
    (formatted, label, index) =>
      index === otherLabels.length - 1
        ? `${formatted} et ${label}`
        : `${formatted}, ${label}`,
    firstLabel,
  );
}

// For each aggregated line of a level that covers several VAT rates, a VAT
// complement line will be added - VAT guard of
// docs/spec/00-project-spec.md §"Total per group" -> "Accounting aggregation
// level" / "VAT warnings". A Manual-total group always counts as one share
// at the VAT of its manual total, so it never triggers a warning on its own.
export function computeAggregationLevelVatWarnings({
  groups,
  resolveVatRateLabel,
}: Readonly<{
  groups: readonly AggregationLevelGroupInput[];
  resolveVatRateLabel: ResolveVatRateLabel;
}>): AggregationLevelVatWarnings {
  const groupWarnings = groups
    .map((group) => {
      const vatIds = distinctInOrder(resolveGroupVatIds(group));

      return vatIds.length > 1
        ? `Le groupe ${group.label} mélange ${formatVatRateList(vatIds, resolveVatRateLabel)} : une ligne de complément TVA sera ajoutée.`
        : null;
    })
    .filter((warning): warning is string => warning !== null);

  const globalVatIds = distinctInOrder(groups.flatMap(resolveGroupVatIds));

  const globalWarnings =
    globalVatIds.length > 1
      ? [
          `Le devis mélange ${formatVatRateList(globalVatIds, resolveVatRateLabel)} : une ligne de complément TVA sera ajoutée.`,
        ]
      : [];

  return {
    // Each usage of an Automatic group keeps its own VAT rate, and each
    // Manual group becomes a single line at its manual total's VAT: no line
    // ever mixes several rates at this level.
    [PennylaneAggregationLevelEnum.ServiceUsage]: [],
    [PennylaneAggregationLevelEnum.Group]: groupWarnings,
    [PennylaneAggregationLevelEnum.Global]: globalWarnings,
  };
}
